import { GlobalContext } from "#/api/global.context";
import { STORAGE } from "#/constants";
import { decodeJwtPayload, UserLevel } from "#/utils/jwt";
import { request } from "#/utils/request";
import { useContext } from "react";

export interface UserSession {
  token: string;
  level?: UserLevel;
  sub?: string;
}

enum USER_STATUS {
  PASSWORD,
  CPF,
  RG,
  COMPLETED,
}

interface UserStatus {
  status: USER_STATUS[];
}

interface LoginResponse {
  token: string;
}

export class UserService {
  static async updateCustomer(data: any) {
    try {
      return await request
        .post(`v1/public/customer`, {
          json: data,
        })
        .json();
    } catch (error) {
      throw await error.response.json();
    }
  }

  static async checkStatus(
    username: string,
    recaptchaToken: string
  ): Promise<UserStatus> {
    try {
      return await request
        .get(`v1/public/customer/${username}/status`, {
          headers: { Authorization: recaptchaToken },
        })
        .json();
    } catch (error) {
      throw await error.response.json();
    }
  }

  static async checkDocument(
    username: string,
    documentType: string,
    documentNumber: string
  ): Promise<string[]> {
    try {
      return await request
        .get(
          `v1/public/customer/${username}/status/${documentType.toLocaleLowerCase()}/${documentNumber.replace(
            /\D/g,
            ""
          )}`
        )
        .json();
    } catch (error) {
      throw await error.response.json();
    }
  }

  static async login(username: string, password: string): Promise<UserSession> {
    try {
      const { token } = await request
        .post(`v1/login`, {
          json: {
            password,
            username,
          },
        })
        .json<LoginResponse>();
      const payload = decodeJwtPayload(token);
      return { token, level: payload?.level, sub: payload?.sub };
    } catch (error) {
      throw await error.response.json();
    }
  }

  static getSession(): UserSession | undefined {
    try {
      const data = sessionStorage.getItem(STORAGE.TOKEN_KEY);
      return JSON.parse(data || "");
    } catch (error) {
      return;
    }
  }

  static getImpersonatorSession(): UserSession | undefined {
    try {
      const data = sessionStorage.getItem(STORAGE.IMPERSONATOR_TOKEN_KEY);
      return JSON.parse(data || "");
    } catch (error) {
      return;
    }
  }

  static async impersonate(username: string): Promise<UserSession> {
    try {
      const { token } = await request
        .get(`v1/admin/impersonate/${encodeURIComponent(username)}`)
        .json<LoginResponse>();
      const payload = decodeJwtPayload(token);
      return { token, level: payload?.level, sub: payload?.sub };
    } catch (error) {
      throw await error.response.json();
    }
  }

  static async encryptPassword(password: string): Promise<string> {
    try {
      const { password: encrypted } = await request
        .post(`v1/admin/encrypt`, { json: { password } })
        .json<{ password: string }>();
      return encrypted;
    } catch (error) {
      throw await error.response.json();
    }
  }

  static isLogged() {
    const { state } = useContext(GlobalContext);
    return !!state.session;
  }
}
